import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, Eye, MousePointer, BarChart3 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { guardedSupabaseQuery } from '@/utils/supabaseGuard';
import { productionLogger } from '@/utils/production-logger';

export function AdvertisingStatsWidget() {
  const [stats, setStats] = useState({
    impressions: 0,
    clicks: 0,
    ctr: 0,
    spend: 0,
    loading: true,
    error: null as string | null
  });

  useEffect(() => {
    const fetchAdvertisingStats = async () => {
      try {
        // Enhanced Security: Check rate limiting before proceeding
        const { data: rateLimitCheck, error: rateLimitError } = await supabase
          .rpc('check_admin_dashboard_rate_limit');
          
        if (rateLimitError) {
          throw new Error(`Rate limit check failed: ${rateLimitError.message}`);
        }
        
        if (!rateLimitCheck) {
          throw new Error('Rate limit exceeded for advertising stats widget access');
        }

        // Enhanced Security: Log widget access for audit trail
        const { error: logError } = await supabase
          .rpc('log_dashboard_widget_access', {
            p_widget_type: 'advertising_stats',
            p_data_points: 1
          });
          
        if (logError) {
          productionLogger.warn('Failed to log advertising widget access:', logError);
          // Continue despite logging failure - don't block widget functionality
        }

        // Get today's visitor data as proxy for advertising metrics
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        const [sessionsResult, productViewsResult] = await Promise.all([
          guardedSupabaseQuery(
            async () => supabase
              .from('user_sessions')
              .select('id, pages_viewed')
              .gte('created_at', startOfDay.toISOString()),
            { fallbackData: [], errorMessage: 'Unable to load session data' }
          ),
          guardedSupabaseQuery(
            async () => supabase
              .from('product_views')
              .select('id')
              .gte('viewed_at', startOfDay.toISOString()),
            { fallbackData: [], errorMessage: 'Unable to load product views data' }
          )
        ]);

        const sessions = sessionsResult.data || [];
        const productViews = productViewsResult.data || [];
        
        // Mock advertising metrics based on actual traffic
        const impressions = sessions.length * 45; // Assume 45 ad impressions per session
        const clicks = productViews.length; // Product views as ad clicks
        const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
        const spend = clicks * 0.85; // $0.85 average CPC

        setStats({
          impressions,
          clicks,
          ctr,
          spend,
          loading: false,
          error: null
        });
      } catch (err) {
        productionLogger.error('Failed to fetch advertising stats:', err);
        setStats(prev => ({
          ...prev,
          loading: false,
          error: err instanceof Error && err.message.includes('Rate limit exceeded') 
            ? 'Rate limit exceeded - please wait before refreshing'
            : err instanceof Error && err.message.includes('Rate limit check failed')
            ? 'Security check failed - contact administrator'
            : 'Unable to load advertising data'
        }));
      }
    };

    fetchAdvertisingStats();
  }, []);

  if (stats.loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="w-4 h-4" />
            Ad Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-16"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (stats.error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="w-4 h-4 text-destructive" />
            Ad Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-destructive text-sm">{stats.error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Target className="w-4 h-4 text-info" />
          Ad Performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Eye className="w-3 h-3" />
              Impressions
            </span>
            <span className="text-sm font-semibold">
              {stats.impressions.toLocaleString()}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MousePointer className="w-3 h-3" />
              Clicks
            </span>
            <span className="text-sm font-medium">
              {stats.clicks}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              CTR
            </span>
            <span className="text-sm font-medium">
              {stats.ctr.toFixed(2)}%
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Spend</span>
            <span className="text-sm font-medium text-accent">
              ${stats.spend.toFixed(2)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}